/**
 * TaskDrawerFollowers Component
 *
 * Renders the followers card in the drawer sidebar including:
 * - Followers list with avatars
 * - Follow/unfollow toggle button
 * - Empty state message
 *
 * Accessibility:
 * - ARIA live region for follower count changes
 * - Proper button labels for screen readers
 * - Keyboard navigable avatar list
 *
 * @see WCAG 2.1 AA - 4.1.2 Name, Role, Value
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Tooltip, message } from 'antd';
import {
  EyeOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import '../../styles/index.scss';

const TaskDrawerFollowers = ({
  currentTask,
  taskId,
  currentUser,
  isFollowers,
  isCurrentUserAssignee,
  isCollaborator,
  updateTaskLoading,
  updateTask,
  fetchTask,
  onTaskUpdate,
  executeWithUnsavedCheck,
}) => {
  const isCreatedBy = currentUser.id === currentTask?.createdBy?.id;
  const followerCount = currentTask?.followers?.length || 0;

  const handleFollowToggle = () => {
    // Check if user is already following by default
    if (!isFollowers && (isCurrentUserAssignee || isCollaborator || isCreatedBy)) {
      if (isCurrentUserAssignee) {
        message.warning('Assignees already follow tasks by default.');
      } else if (isCollaborator) {
        message.warning('Collaborators already follow tasks by default.');
      } else if (isCreatedBy) {
        message.warning('You are the creator of this task and already follow it by default.');
      }
      return;
    }

    executeWithUnsavedCheck(async () => {
      try {
        await updateTask({
          variables: {
            data: {
              followers: !isFollowers
                ? { connect: { id: currentUser.id } }
                : { disconnect: { id: currentUser.id } },
            },
            where: { id: taskId },
          },
        });
        await fetchTask({
          variables: { where: { id: taskId } },
        });
        message.success(!isFollowers ? 'You followed the task.' : 'Unfollowed task.');
        if (onTaskUpdate) {
          onTaskUpdate();
        }
      } catch (error) {
        message.error(!isFollowers ? 'Failed to follow.' : 'Failed to unfollow task.');
      }
    });
  };

  return (
    <Card
      size="small"
      className="task-drawer-full__card"
      role="region"
      aria-labelledby="followers-heading"
    >
      <div className="task-drawer-full__card-header">
        <div className="task-drawer-full__card-title">
          <EyeOutlined
            className="task-drawer-full__card-icon--primary"
            aria-hidden="true"
          />
          <span id="followers-heading">Followers</span>
          <span className="sr-only">
            {followerCount} {followerCount === 1 ? 'person' : 'people'} following
          </span>
        </div>
        <Button
          type="text"
          size="small"
          onClick={handleFollowToggle}
          disabled={updateTaskLoading}
          icon={isFollowers ? <MinusOutlined /> : <PlusOutlined />}
          className={
            isFollowers
              ? 'task-drawer-full__card-action--unfollow'
              : 'task-drawer-full__card-action--follow'
          }
          aria-label={isFollowers ? 'Unfollow this task' : 'Follow this task'}
          aria-pressed={isFollowers}
        >
          {isFollowers ? 'Unfollow' : 'Follow'}
        </Button>
      </div>

      {currentTask?.followers && currentTask.followers.length > 0 ? (
        <div
          className="task-drawer-full__avatar-stack"
          role="list"
          aria-label="Task followers"
        >
          {currentTask.followers.map((follower) => (
            <Tooltip
              key={follower.id}
              title={startCase(follower.name || follower.username)}
            >
              <div role="listitem">
                <AvatarS3
                  imgKey={follower.avatar?.key}
                  username={follower.name || follower.username}
                  avatarProps={{
                    size: 28,
                    'aria-label': startCase(follower.name || follower.username),
                  }}
                />
              </div>
            </Tooltip>
          ))}
        </div>
      ) : (
        <Text type="secondary" className="task-drawer-full__empty-text">
          No followers yet
        </Text>
      )}
    </Card>
  );
};

TaskDrawerFollowers.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    followers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        username: PropTypes.string,
        avatar: PropTypes.shape({
          key: PropTypes.string,
        }),
      })
    ),
    createdBy: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  taskId: PropTypes.string.isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  isFollowers: PropTypes.bool,
  isCurrentUserAssignee: PropTypes.bool,
  isCollaborator: PropTypes.bool,
  updateTaskLoading: PropTypes.bool,
  updateTask: PropTypes.func.isRequired,
  fetchTask: PropTypes.func.isRequired,
  onTaskUpdate: PropTypes.func,
  executeWithUnsavedCheck: PropTypes.func.isRequired,
};

TaskDrawerFollowers.defaultProps = {
  currentTask: null,
  isFollowers: false,
  isCurrentUserAssignee: false,
  isCollaborator: false,
  updateTaskLoading: false,
  onTaskUpdate: null,
};

export default TaskDrawerFollowers;
